import { normalizeLinkInBioUrl } from './normalizeLinkInBioUrl';
import type { LinkInBioSocialPlatform } from './types';

type SocialFormat =
  | { kind: 'handle'; prefix: string; base: string; hosts: string[]; pathPrefix?: string }
  | { kind: 'path'; prefix: string; base: string; hosts: string[] }
  | { kind: 'phone'; prefix: string; base: string; hosts: string[] }
  | { kind: 'email' }
  | { kind: 'url' };

export const LINK_IN_BIO_SOCIAL_FORMATS: Record<LinkInBioSocialPlatform, SocialFormat> = {
  instagram: {
    kind: 'handle',
    prefix: 'instagram.com/',
    base: 'https://instagram.com/',
    hosts: ['instagram.com'],
  },
  facebook: {
    kind: 'handle',
    prefix: 'facebook.com/',
    base: 'https://facebook.com/',
    hosts: ['facebook.com', 'fb.com'],
  },
  tiktok: {
    kind: 'handle',
    prefix: 'tiktok.com/@',
    base: 'https://www.tiktok.com/@',
    hosts: ['tiktok.com'],
    pathPrefix: '@',
  },
  youtube: {
    kind: 'handle',
    prefix: 'youtube.com/@',
    base: 'https://youtube.com/@',
    hosts: ['youtube.com'],
    pathPrefix: '@',
  },
  x: { kind: 'handle', prefix: 'x.com/', base: 'https://x.com/', hosts: ['x.com', 'twitter.com'] },
  linkedin: {
    kind: 'path',
    prefix: 'linkedin.com/',
    base: 'https://linkedin.com/',
    hosts: ['linkedin.com'],
  },
  whatsapp: { kind: 'phone', prefix: 'wa.me/', base: 'https://wa.me/', hosts: ['wa.me'] },
  email: { kind: 'email' },
  website: { kind: 'url' },
};

export const LINK_IN_BIO_SOCIAL_ERRORS: Record<LinkInBioSocialPlatform, string> = {
  instagram: 'Enter an Instagram username or profile link',
  facebook: 'Enter a Facebook page name or link',
  tiktok: 'Enter a TikTok username or profile link',
  youtube: 'Enter a YouTube handle or channel link',
  x: 'Enter an X username or profile link',
  linkedin: 'Enter a LinkedIn link, like company/your-name or in/your-name',
  whatsapp: 'Enter a phone number with country code',
  email: 'Enter an email address',
  website: 'Enter a web address',
};

const HANDLE = /^[A-Za-z0-9._-]{1,100}$/;
const LINKEDIN_PATH = /^(in|company|school|showcase)\/[A-Za-z0-9._%-]{1,100}\/?$/;
const SCHEME = /^[a-z][a-z0-9+.-]*:/i;

const looksLikeUrl = (value: string) =>
  SCHEME.test(value) || value.includes('/') || /^www\./i.test(value);

export function toLinkInBioSocialUrl(
  platform: LinkInBioSocialPlatform,
  input: string | null | undefined,
) {
  const value = (input ?? '').trim();
  if (!value) return null;
  const format = LINK_IN_BIO_SOCIAL_FORMATS[platform];

  switch (format.kind) {
    case 'email':
      return normalizeLinkInBioUrl(value, { allowEmail: true });
    case 'url':
      return normalizeLinkInBioUrl(value);
    case 'phone': {
      if (looksLikeUrl(value)) return normalizeLinkInBioUrl(value);
      const digits = value.replace(/[\s().+-]/g, '');
      return /^\d{6,15}$/.test(digits) ? `${format.base}${digits}` : null;
    }
    case 'path':
      if (LINKEDIN_PATH.test(value)) return `${format.base}${value.replace(/\/$/, '')}`;
      return looksLikeUrl(value) ? normalizeLinkInBioUrl(value) : null;
    case 'handle': {
      if (looksLikeUrl(value)) return normalizeLinkInBioUrl(value);
      const handle = value.replace(/^@/, '');
      return HANDLE.test(handle) ? `${format.base}${handle}` : null;
    }
  }
}

export function toLinkInBioSocialInput(
  platform: LinkInBioSocialPlatform,
  url: string | null | undefined,
) {
  const value = (url ?? '').trim();
  const format = LINK_IN_BIO_SOCIAL_FORMATS[platform];
  if (format.kind === 'email') return value.replace(/^mailto:/i, '');
  if (format.kind === 'url' || !value) return value;

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return value;
  }

  const host = parsed.hostname.replace(/^(www|m)\./, '');
  if (!format.hosts.includes(host) || parsed.search || parsed.hash) return value;

  const path = parsed.pathname.replace(/^\//, '').replace(/\/$/, '');
  if (format.kind === 'path') return LINKEDIN_PATH.test(path) ? path : value;
  if (format.kind === 'phone') return /^\d{6,15}$/.test(path) ? path : value;

  const handle = format.pathPrefix ? path.replace(new RegExp(`^${format.pathPrefix}`), '') : path;
  return HANDLE.test(handle) && (!format.pathPrefix || path.startsWith(format.pathPrefix))
    ? handle
    : value;
}
